import { useState, useEffect } from 'react'
import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@/i18n'

export interface DuplicateFileModalProps {
  isOpen: boolean
  onClose: () => void
  fileName: string
  onReplace: () => void
  onKeepBoth: () => void
}

export type DuplicateAction = 'replace' | 'keep-both'

const DuplicateFileModal = ({
  isOpen,
  onClose,
  fileName,
  onReplace,
  onKeepBoth,
}: DuplicateFileModalProps) => {
  const { t } = useTranslation()
  const [selectedAction, setSelectedAction] = useState<DuplicateAction | null>(null)

  useEffect(() => {
    if (isOpen) {
      setSelectedAction(null)
    }
  }, [isOpen])

  const handleUpload = () => {
    if (selectedAction === 'replace') {
      onReplace()
    } else if (selectedAction === 'keep-both') {
      onKeepBoth()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('data_catalog:duplicate_modal.title')}
      overlayClassName="bg-black/10"
    >
      <div className="space-y-4">
        {/* Warning message */}
        <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-gray-700 dark:text-gray-300 break-all">
            {t('data_catalog:duplicate_modal.message', { fileName })}
          </p>
        </div>

        {/* Radio button options */}
        <div className="space-y-3">
          {/* Option 1: Replace */}
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors min-w-0 overflow-hidden ${
              selectedAction === 'replace'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <input
              type="radio"
              name="duplicate-action"
              value="replace"
              checked={selectedAction === 'replace'}
              onChange={() => setSelectedAction('replace')}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-dark-text">
                {t('data_catalog:duplicate_modal.option_replace')}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {t('data_catalog:duplicate_modal.option_replace_description')}
              </div>
            </div>
          </label>

          {/* Option 2: Keep both */}
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors min-w-0 overflow-hidden ${
              selectedAction === 'keep-both'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <input
              type="radio"
              name="duplicate-action"
              value="keep-both"
              checked={selectedAction === 'keep-both'}
              onChange={() => setSelectedAction('keep-both')}
              className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 focus:ring-blue-500 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-gray-900 dark:text-dark-text">
                {t('data_catalog:duplicate_modal.option_keep_both')}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                {t('data_catalog:duplicate_modal.option_keep_both_description')}
              </div>
            </div>
          </label>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:buttons.cancel')}
          </Button>
          <Button onClick={handleUpload} disabled={!selectedAction}>
            {t('common:buttons.upload')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default DuplicateFileModal
